/**
 * GuardGroupedList
 *
 * Grouped list view for guards, organized by customer/site.
 * Used when there are 30+ guards to improve organization.
 *
 * Features:
 * - Groups guards by customer
 * - Expandable/collapsible groups
 * - Alert count per group
 * - Search works across all groups
 * - Remembers expanded state in session storage
 *
 * Visual Design:
 * ┌─ GUARDS ────────────────────────────┐
 * │  ▼ Downtown Office (4)        ⚠️ 1 │
 * │      John Doe            🔋 85%     │
 * │      Jane Smith          🔋 72%     │
 * │  ► Mall Security (6)          ⚠️ 0 │
 * │  ▼ Airport Terminal (3)       ⚠️ 2 │
 * └─────────────────────────────────────┘
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, Collapse, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningIcon from '@mui/icons-material/Warning';
import styles from './GuardGroupedList.module.css';
import { GuardLocation, STATUS_COLORS } from './types';
import GuardCard from './GuardCard';
import classNames from 'classnames';

// ============================================
// Types
// ============================================

/** Group data structure */
export interface GuardGroup {
  customerId: number;
  customerName: string;
  guards: GuardLocation[];
  guardCount: number;
  alertCount: number;
}

/** Threshold for switching to grouped view */
export const GROUPING_THRESHOLD = 30;

// ============================================
// Session Storage Keys
// ============================================

const STORAGE_KEY = 'guard-grouped-list-expanded';

/** Get expanded state from session storage */
function getStoredExpandedState(): Record<number, boolean> {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/** Save expanded state to session storage */
function saveExpandedState(state: Record<number, boolean>): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * Groups guards by customer and calculates alert counts
 */
export function groupGuardsByCustomer(guards: GuardLocation[]): GuardGroup[] {
  const groupMap = new Map<number, GuardGroup>();

  guards.forEach((guard) => {
    const customerId = guard.currentPatrol?.customerId || 0;
    const customerName = guard.currentPatrol?.customerName || 'Not on Patrol';

    if (!groupMap.has(customerId)) {
      groupMap.set(customerId, {
        customerId,
        customerName,
        guards: [],
        guardCount: 0,
        alertCount: 0,
      });
    }

    const group = groupMap.get(customerId)!;
    group.guards.push(guard);
    group.guardCount++;

    // Count alerts: low battery, stale, or offline on patrol
    const hasAlert =
      (guard.batteryPercent !== null && guard.batteryPercent < 20) ||
      guard.status === 'stale' ||
      (guard.status === 'offline' && guard.currentPatrol !== null);
    if (hasAlert) {
      group.alertCount++;
    }
  });

  // Sort groups: "Not on Patrol" last, then by customer name
  return Array.from(groupMap.values()).sort((a, b) => {
    if (a.customerId === 0) return 1;
    if (b.customerId === 0) return -1;
    return a.customerName.localeCompare(b.customerName);
  });
}

/**
 * Check if a guard matches the search query
 */
function guardMatchesSearch(guard: GuardLocation, query: string): boolean {
  const searchLower = query.toLowerCase();
  return (
    guard.name.toLowerCase().includes(searchLower) ||
    (guard.currentPatrol?.customerName?.toLowerCase().includes(searchLower) ?? false) ||
    (guard.currentPatrol?.siteName?.toLowerCase().includes(searchLower) ?? false)
  );
}

// ============================================
// Sub-components
// ============================================

interface GroupHeaderProps {
  group: GuardGroup;
  isExpanded: boolean;
  onToggle: () => void;
  matchCount?: number;
}

const GroupHeader: React.FC<GroupHeaderProps> = ({
  group,
  isExpanded,
  onToggle,
  matchCount,
}) => {
  const showMatchCount = matchCount !== undefined && matchCount !== group.guardCount;

  return (
    <Box
      className={classNames(styles.groupHeader, { [styles.expanded]: isExpanded })}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      aria-expanded={isExpanded}
    >
      <Box className={styles.headerLeft}>
        <IconButton size="small" className={styles.expandIcon}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <Typography variant="body2" className={styles.groupName}>
          {group.customerName}
        </Typography>
        <Typography variant="caption" className={styles.groupCount}>
          ({showMatchCount ? `${matchCount}/` : ''}{group.guardCount})
        </Typography>
      </Box>

      {group.alertCount > 0 && (
        <Tooltip title={`${group.alertCount} alert${group.alertCount !== 1 ? 's' : ''}`}>
          <Box className={styles.alertIndicator}>
            <WarningIcon className={styles.alertIcon} />
            <span>{group.alertCount}</span>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

// ============================================
// Main Component
// ============================================

export interface GuardGroupedListProps {
  /** Guards to display */
  guards: GuardLocation[];
  /** Search query to filter guards */
  searchQuery?: string;
  /** Currently selected guard ID */
  selectedGuardId?: number | null;
  /** Callback when a guard is selected */
  onGuardSelect?: (guardId: number) => void;
  /** Whether to show checkpoint progress on cards */
  showProgress?: boolean;
  /** Force all groups expanded (useful for search) */
  forceExpanded?: boolean;
}

const GuardGroupedList: React.FC<GuardGroupedListProps> = ({
  guards,
  searchQuery = '',
  selectedGuardId = null,
  onGuardSelect,
  showProgress = true,
  forceExpanded = false,
}) => {
  // Ref for scrolling selected guard into view
  const selectedRef = useRef<HTMLDivElement | null>(null);

  // Expanded state for each group (by customerId)
  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>(() =>
    getStoredExpandedState()
  );

  // Group guards by customer
  const groups = useMemo(() => groupGuardsByCustomer(guards), [guards]);

  // Filter guards by search query and track which groups have matches
  const { filteredGroups, groupMatchCounts } = useMemo(() => {
    if (!searchQuery.trim()) {
      return {
        filteredGroups: groups,
        groupMatchCounts: new Map<number, number>(),
      };
    }

    const matchCounts = new Map<number, number>();
    const filtered = groups
      .map((group) => {
        const matchingGuards = group.guards.filter((g) =>
          guardMatchesSearch(g, searchQuery)
        );
        matchCounts.set(group.customerId, matchingGuards.length);
        return {
          ...group,
          guards: matchingGuards,
          guardCount: group.guardCount, // Keep original count for display
        };
      })
      .filter((group) => group.guards.length > 0);

    return { filteredGroups: filtered, groupMatchCounts: matchCounts };
  }, [groups, searchQuery]);

  // Auto-expand groups with search matches
  useEffect(() => {
    if (searchQuery.trim()) {
      const newExpanded = { ...expandedGroups };
      filteredGroups.forEach((group) => {
        newExpanded[group.customerId] = true;
      });
      setExpandedGroups(newExpanded);
    }
  }, [searchQuery, filteredGroups]);

  // Scroll selected guard into view when selection changes
  useEffect(() => {
    if (selectedGuardId !== null && selectedRef.current) {
      selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedGuardId]);

  // Save expanded state to session storage
  useEffect(() => {
    saveExpandedState(expandedGroups);
  }, [expandedGroups]);

  // Toggle group expansion
  const toggleGroup = useCallback((customerId: number) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  }, []);

  // Check if a group is expanded
  const isGroupExpanded = useCallback(
    (customerId: number) => {
      if (forceExpanded) return true;
      return expandedGroups[customerId] ?? false;
    },
    [expandedGroups, forceExpanded]
  );

  // Empty state
  if (filteredGroups.length === 0) {
    return (
      <Box className={styles.emptyState}>
        <Typography variant="body2" color="textSecondary">
          {searchQuery ? 'No guards match your search' : 'No guards to display'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.groupedList}>
      {filteredGroups.map((group) => (
        <Box key={group.customerId} className={styles.group}>
          <GroupHeader
            group={group}
            isExpanded={isGroupExpanded(group.customerId)}
            onToggle={() => toggleGroup(group.customerId)}
            matchCount={groupMatchCounts.get(group.customerId)}
          />
          <Collapse in={isGroupExpanded(group.customerId)}>
            <Box className={styles.groupContent}>
              {group.guards.map((guard) => (
                <div
                  key={guard.guardId}
                  ref={selectedGuardId === guard.guardId ? selectedRef : null}
                >
                  <GuardCard
                    guard={guard}
                    isSelected={selectedGuardId === guard.guardId}
                    onClick={onGuardSelect}
                    showProgress={showProgress}
                    compact
                  />
                </div>
              ))}
            </Box>
          </Collapse>
        </Box>
      ))}

      {/* Summary */}
      <Box className={styles.summary}>
        <Typography variant="caption" color="textSecondary">
          {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''} •{' '}
          {filteredGroups.reduce((sum, g) => sum + g.guards.length, 0)} guard
          {filteredGroups.reduce((sum, g) => sum + g.guards.length, 0) !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </Box>
  );
};

export default GuardGroupedList;
